import {useState, useEffect} from "react";
import {Stack, Button, FormControl, FilledInput, InputAdornment, IconButton, InputLabel, Typography, Paper} from "@mui/material";
import {Visibility, VisibilityOff} from "@mui/icons-material";
import FormHelperText from '@mui/material/FormHelperText';
import {useDispatch} from "react-redux";
import {resetPassword} from "../../../slices/userSlice";
import { useNavigate, useSearchParams} from 'react-router-dom';

export default function ResetPassword() {

    const dispatch = useDispatch(); 
    const navigate = useNavigate();
    // 1. Read it (The lines you just asked about)
    const [searchParams] = useSearchParams();
    const [token, setToken] = useState("");
    const [errors, setErrors] = useState({ newPassword: '', confirmPassword: '' });
    const [newPassword, setNewPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false); //for the re-enter new password box
    const handleClickShowNewPassword = () => setShowNewPassword((show) => !show);
    const handleClickShowConfirmPassword = () => setShowConfirmPassword((show) => !show); //for the re-enter new password box
    const handleChange = (e) => {
    const { name, value } = e.target;

    const updatePasswords = {
      newPassword: name === "newPassword" ? value : newPassword,
      confirmPassword: name === "confirmPassword" ? value : confirmPassword
    };

      if (name === "newPassword") {
        setNewPassword(value);
      } else {
        setConfirmPassword(value);
      }

      const currentFieldError = validateField(name, value, updatePasswords);

      setErrors((prev) => {
        const nextErrors = { ...prev, [name]: currentFieldError };

        if (name === "newPassword" && updatePasswords.confirmPassword) {
          nextErrors.confirmPassword = validateField("confirmPassword", updatePasswords.confirmPassword, updatePasswords);
        }

        return nextErrors;
      });
    };

  const validateField = (name, value, currentPasswords) => {
    switch (name) {
      case 'newPassword':
        if (!value) return "Password is required.";
        if (value.length < 8) return "Password must be at least 8 characters.";
        if (value.length > 64) return "Password must be less than or equal to 64 characters.";
        if (!/[A-Z]/.test(value)) return "Password must contain at least one uppercase letter.";
        if (!/[a-z]/.test(value)) return "Password must contain at least one lowercase letter.";
        if (!/[0-9]/.test(value)) return "Password must contain at least one number.";
        if (!/[!@#$%^&*.?]/.test(value)) return "Password must contain at least one special character.";
        return "";
      case 'confirmPassword':
        if(!value) return "Please confirm your new password.";

        const comparePasswords = currentPasswords ? currentPasswords.newPassword : newPassword
        return value !== comparePasswords ? "Passwords do not match." : "";
      default:
        return "";
    }
  };
    const newPasswordAdornment = (
    <InputAdornment position="end" data-testid="password-adornment">
      <IconButton
        aria-label={showNewPassword ? 'hide password' : 'display password'}
        onClick={handleClickShowNewPassword}
        edge="end"
        data-testid="password-toggle-button"
      >
        {showNewPassword ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  //for the re-enter new password box
  const confirmPasswordAdornment = (
    <InputAdornment position="end" data-testid="new-password-adornment">
        <IconButton
        aria-label={showConfirmPassword ? 'hide confirm password' : 'display confirm password'}
        onClick={handleClickShowConfirmPassword}
        edge="end"
        data-testid="new-password-toggle-button"
        >
            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
    </InputAdornment>
  )
    
        useEffect(() => {
        const style = document.createElement('style');
        style.setAttribute('data-reset-password-style', 'true');
        style.textContent =
          'input[type="password"]::-ms-reveal,input[type="password"]::-ms-clear{display:none}';

        document.head.appendChild(style);

        return () => {
          style.remove();
        };
    }, []);

  useEffect(() => {
    const urlToken = searchParams.get("token");

    if (urlToken) {
      // Save token to sessionStorage
      sessionStorage.setItem("resetToken", urlToken);
      setToken(urlToken);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else {
      // Fallback to sessionStorage if page was refreshed
      const savedToken = sessionStorage.getItem("resetToken");
      if (savedToken) {
        setToken(savedToken);
      }
    }
  }, [searchParams]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const newPasswordError = validateField("newPassword", newPassword);
    const confirmPasswordError = validateField("confirmPassword", confirmPassword);


    const activeToken = token;
    if (newPasswordError || confirmPasswordError) {
      setErrors({ newPassword: newPasswordError, confirmPassword: confirmPasswordError });
      return;
    }

    if(!activeToken) {
      setErrors(prev => ({ ...prev, newPassword: "Link has expired or is invalid, please request a new link." }));
      return;
    }

    const result = await dispatch(resetPassword({
      newPassword,
      token,
    }));
    
    const errorMessage = result.payload || "An error occurred while resetting your password. Please try again.";
    if(resetPassword.fulfilled.match(result)) {
      sessionStorage.removeItem("resetToken"); // Clear the token from sessionStorage after a successful reset
      setPasswordResetSuccess(true);
    } else if (resetPassword.rejected.match(result)) {
      setErrors(prev => ({ ...prev, newPassword: errorMessage }));
    }

  };
  if (passwordResetSuccess) {
    return (
      <Stack
      spacing={4}
      sx={{ maxWidth: 400, margin: 'auto', padding: 4, textAlign: 'center' }}
      component={Paper}
      elevation={4}
      data-testid="password-reset-success-screen"
      >
        <Typography variant="h4">
          Password Changed!
        </Typography>
        <Typography vairant="body1" color="text.secondary">
          Your password has been changed successfully.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please login with your new password.
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate("/login")}
          sx={{ mt: 3, backgroundColor: "#52219E"}}
          data-testid="navigate-to-login-button"
        >
          Return to Login
        </Button>
      </Stack>
    )
  }
    return (
        <div className="password-reset-page" data-testid="password-reset-page">
            <div className="password-reset-wrapper" data-testid="password-reset-wrapper">
                <div className="form-container" data-testid="form-container">
                    <Stack
                    spacing={2}
                    component="form"
                    onSubmit={handleSubmit}
                    data-testid="password-reset-form"
                    noValidate
                    >
                        <h1 className="WelcomeHeader" data-testid="welcome-header">Insert New Password</h1>

                        <FormControl variant="filled" fullWidth size="small" required data-testid="password-form-control">
                            <InputLabel htmlFor="newPassword" data-testid="password-label">New Password</InputLabel>
                            <FilledInput
                                id="newPassword"
                                data-testid="password-input"
                                name="newPassword"
                                type={showNewPassword ? 'text' : 'password'}
                                value={newPassword}
                                onChange={handleChange}
                                endAdornment={newPasswordAdornment}
                                aria-label="Display New Password"
                                ></FilledInput>
                                {errors.newPassword && <FormHelperText error data-testid="error-label">{errors.newPassword}</FormHelperText>}
                        </FormControl>

                        <FormControl variant="filled" fullWidth size="small" required data-testid="new-password-form-control">
                            <InputLabel htmlFor="confirmPassword" data-testid="new-password-label">Re-enter New Password</InputLabel>
                            <FilledInput
                                id="confirmPassword"
                                data-testid="new-password-input"
                                name="confirmPassword"
                                type={showConfirmPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={handleChange}
                                endAdornment={confirmPasswordAdornment}
                                label="Re-enter New Password"
                                ></FilledInput>
                                {errors.confirmPassword && <FormHelperText error>{errors.confirmPassword}</FormHelperText>}
                        </FormControl>

                        <Button
                            variant="contained"
                            color="#51219D"
                            size="small"
                            type="submit"
                            data-testid="reset-password-button"
                            >
                                Reset Password
                            </Button>
                    </Stack>
                </div>
            </div>
        </div>
    )
}